/**
 * The three answers of "Price curve of a date" (CU-869fmjzrg D-B, CA 10-12), read from the curve's lines (the visible
 * providers; `buildDateCurve`), so Tier / Providers change them without asking. Rules of the approved mock.
 *
 * Pure and alias-free: `node --test` runs it.
 */
import { rankOf } from '../../../@services/stats.js'

// Curro's decision 5: a price has moved when it changes 5 % or more against its previous capture day with a price.
// Fixed, said on the card, and the rule the future price-jump alerts reuse
export const MOVE_THRESHOLD = 0.05
const EPS = 1e-9

const byDistance = points => [...(points || [])].sort((a, b) => b.d - a.d)
const providerLines = curve => [...(curve?.you ? [curve.you] : []), ...(curve?.others || [])]
const change = (from, to) => ({ eur: to.value - from.value, pct: from.value ? to.value / from.value - 1 : null })

/** CA 10: for each provider, 7 days before its last capture → its last capture; biggest drop first, `none` last */
export function last7(curve) {
	const rows = providerLines(curve).map(line => {
		const points = byDistance(line.points)
		const now = points.at(-1)
		if (!now) return { line, none: true, noPrice: true }
		const before = points.filter(p => p.d >= now.d + 7).at(-1)
		if (!before) return { line, now, none: true }
		return { line, now, before, none: false, ...change(before, now), thin: !!(now.thin || before.thin) }
	})
	return rows.sort((a, b) => (a.none ? 1 : 0) - (b.none ? 1 : 0) || (a.pct ?? 0) - (b.pct ?? 0) || String(a.line.name).localeCompare(String(b.line.name)))
}

function moves(curve) {
	const out = []
	for (const line of providerLines(curve)) {
		const points = byDistance(line.points)
		for (let i = 1; i < points.length; i++) {
			const from = points[i - 1], to = points[i]
			const c = change(from, to)
			if (c.pct === null) continue
			out.push({ line, d: to.d, from, to, captured: to.captured || null, thin: !!(from.thin || to.thin), ...c })
		}
	}
	return out
}

/**
 * CA 11: the first provider(s) whose price moved 5 % or more against its previous capture day (the largest distance
 * before the pick-up; ties share it), and who followed in the same direction in the next 7 days (each once, its first
 * such move). `first: []` = nobody; `biggest` = the biggest daily move, for "Nobody moved 5 % or more".
 */
export function whoMovedFirst(curve) {
	const all = moves(curve)
	const big = all.filter(m => Math.abs(m.pct) >= MOVE_THRESHOLD - EPS).sort((a, b) => b.d - a.d || Math.abs(b.pct) - Math.abs(a.pct))
	const biggest = [...all].sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct))[0] || null
	if (!big.length) return { first: [], followers: [], d: null, biggest }

	const d = big[0].d
	const first = big.filter(m => m.d === d)
	const dir = Math.sign(first[0].pct)
	const seen = new Set(first.map(m => m.line.key))
	const followers = []
	for (const m of big) {
		if (m.d >= d || m.d < d - 7 || Math.sign(m.pct) !== dir || seen.has(m.line.key)) continue
		seen.add(m.line.key)
		followers.push(m)
	}
	return { first, followers, d, biggest }
}

/**
 * CA 12, the rule of "Your position" in Season vs last year: on the last capture day, your rank among you and the
 * competitors with a price (your other brands out), 1 = the cheapest, ties share it; the gap to the cheapest, or how
 * much below the second when you are first. `null` without identity; `{ alone: true }` without a competitor that day.
 */
export function positionAt(curve) {
	if (!curve?.you) return null
	const lines = providerLines(curve)
	const days = lines.flatMap(l => l.points.map(p => p.d))
	if (!days.length) return null
	const at = (line, d) => line.points.find(p => p.d === d) || null

	let d = Math.min(...days)
	let yp = at(curve.you, d)
	if (!yp) {
		yp = byDistance(curve.you.points).at(-1) || null
		if (!yp) return { none: true }
		d = yp.d
	}
	const competitors = (curve.others || []).filter(l => !l.own)
	const priced = competitors.map(line => ({ line, p: at(line, d) })).filter(x => x.p)
	if (!priced.length) return { alone: true, d, you: yp.value }

	const rank = rankOf(priced.map(x => x.p.value), yp.value)
	const sorted = priced.sort((a, b) => a.p.value - b.p.value)
	const ref = rank === 1 ? sorted.find(x => x.p.value >= yp.value) || sorted[0] : sorted[0]
	const eur = rank === 1 ? ref.p.value - yp.value : yp.value - ref.p.value
	const median = at(curve.median, d)
	return {
		d, rank, of: priced.length + 1, you: yp.value, thin: !!yp.thin,
		gap: { name: ref.line.name, eur, pct: ref.p.value ? eur / ref.p.value : null, kind: rank === 1 ? 'below' : 'above' },
		median: median ? median.value : null,
		noPrice: competitors.length - priced.length,
	}
}

export const CARD_ROWS = 5
export const CARD_FOLLOWERS = 3

/**
 * Last 7 days, compact (Curro's review: ~30 rows pushed the curve to the bottom): the `n` biggest changes by size,
 * up or down, and your brand always, in the card's order (biggest drop first). `{ rows, hidden }`, `hidden` = how many
 * more the "Show all" opens.
 */
export function capLast7(rows, n = CARD_ROWS) {
	const moved = rows.filter(r => !r.none)
	const top = new Set([...moved].sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct) || String(a.line.name).localeCompare(String(b.line.name))).slice(0, n).map(r => r.line.key))
	const kept = rows.filter(r => top.has(r.line.key) || r.line.role === 'you')
	return { rows: kept, hidden: rows.length - kept.length }
}

/** Who moved first, compact: the first mover and at most `followers` followers; `hidden` = the rest */
export function capMoves(moved, followers = CARD_FOLLOWERS) {
	const first = moved.first.slice(0, 1)
	const kept = moved.followers.slice(0, followers)
	return { ...moved, first, followers: kept, hidden: moved.first.length - first.length + moved.followers.length - kept.length }
}
